import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  Phone,
  MapPin,
  ShieldCheck,
  Factory,
  Sparkles,
  Cpu,
  Award,
  Cog,
  Wrench,
  Users,
  Home,
  Package,
  HeartPulse,
  MoveUp,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { BUSINESS, MEDIA } from "@/constants/business";
import { PRODUCTS } from "@/constants/products";
import { POSTS } from "@/constants/blog";
import ContactForm from "@/components/ContactForm";

// ─── STYLES SCOPED TO THIS PREVIEW PAGE ────────────────────────────────
const wrapperStyle = {
  background: "#faf7f2",
  color: "#1a1a1a",
  fontFamily: "'Inter', -apple-system, sans-serif",
};

const productIcons = { Users, Home, Package, HeartPulse, MoveUp, MoveRight: ArrowRight };

const heroSlides = [
  { title: "PASSENGER", subtitle: "Whisper-quiet vertical elegance", image: MEDIA.productElevator },
  { title: "HOME LIFT", subtitle: "Compact luxury for private residences", image: MEDIA.hero },
  { title: "FREIGHT", subtitle: "Heavy-duty industrial precision", image: MEDIA.hero },
  { title: "ESCALATOR", subtitle: "Continuous flow, uncompromised safety", image: MEDIA.productEscalator },
];

export default function HomePageV2() {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={wrapperStyle} className="min-h-screen">
      <Helmet>
        <title>Preview V2 · Vila Elevators</title>
      </Helmet>

      {/* Style overrides for this page only */}
      <style>{`
        .v2-serif { font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif; }
        .v2-italic-serif { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; }
        .v2-forest { color: #0f5132; }
        .v2-bg-cream { background: #f5efe6; }
        .v2-bg-ivory { background: #faf7f2; }
        .v2-btn-primary {
          background: #0f5132; color: #fff; padding: 16px 32px; font-size: 12px;
          letter-spacing: 3px; text-transform: uppercase; font-weight: 500;
          transition: all .35s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid #0f5132;
        }
        .v2-btn-primary:hover { background: transparent; color: #0f5132; }
        .v2-btn-secondary {
          background: transparent; color: #1a1a1a; padding: 16px 32px; font-size: 12px;
          letter-spacing: 3px; text-transform: uppercase; font-weight: 500;
          border: 1px solid #1a1a1a; transition: all .35s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .v2-btn-secondary:hover { background: #1a1a1a; color: #fff; }
        .v2-eyebrow { font-size: 11px; letter-spacing: 4px; text-transform: uppercase; color: #0f5132; font-weight: 500; }
        .v2-hairline { height: 1px; background: rgba(15,81,50,0.2); }
        .v2-preview-badge {
          position: fixed; top: 12px; left: 12px; z-index: 100;
          background: #0f5132; color: #fff; padding: 6px 14px; font-size: 11px;
          letter-spacing: 2px; text-transform: uppercase; font-weight: 600;
        }
      `}</style>

      <div className="v2-preview-badge" data-testid="preview-badge">
        Preview V2 · Luxury Light Theme
      </div>

      {/* ═══════════════════ HERO — Fullscreen Slider ═══════════════════ */}
      <section className="relative h-screen w-full overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <img src={heroSlides[slide].image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(15,20,25,0.55), rgba(15,20,25,0.15) 60%, transparent)" }} />
          </motion.div>
        </AnimatePresence>

        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-32">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-[#1a1a1a]"
            >
              <div className="v2-eyebrow mb-6" style={{ color: "#c9a961" }}>Innovation in Motion</div>
              <h1 className="v2-serif font-light leading-[0.9] tracking-tight" style={{ fontSize: "clamp(4rem, 10vw, 9rem)" }}>
                {heroSlides[slide].title}
              </h1>
              <p className="v2-italic-serif text-2xl md:text-3xl mt-6 mb-10 text-[#1a1a1a]/80 max-w-2xl">
                {heroSlides[slide].subtitle}
              </p>
              <div className="flex gap-4">
                <Link to="/contact" className="v2-btn-primary inline-block" style={{ background: "#c9a961", borderColor: "#c9a961" }}>
                  Request A Quote
                </Link>
                <Link to="/products" className="v2-btn-secondary inline-block" style={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)" }}>
                  Explore Range
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide dots */}
        <div className="absolute bottom-12 right-12 flex gap-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              className={`h-[2px] transition-all duration-500 ${i === slide ? "w-16 bg-white" : "w-8 bg-[#1a1a1a]/40"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════════ WELCOME / INTRO ═══════════════════ */}
      <section className="py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <div className="v2-eyebrow mb-8">Welcome to Vila Elevators</div>
            <h2 className="v2-serif font-light leading-[1.1] text-4xl md:text-6xl mb-10" style={{ color: "#1a1a1a" }}>
              Unparalleled Craftsmanship,<br />
              <span className="v2-italic-serif font-normal" style={{ color: "#0f5132" }}>engineered in Bangalore.</span>
            </h2>
            <div className="v2-hairline w-24 mx-auto mb-10" />
            <p className="text-lg md:text-xl text-[#8a8a8a] leading-relaxed max-w-3xl mx-auto font-light">
              In an era of urban sophistication, Vila stands as a testament to South India's engineering excellence. Every elevator we ship embodies quality, safety, and a commitment to unmatched service — <em className="v2-italic-serif">from raw steel to installed opulence, entirely under one roof.</em>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-24">
            {[
              { icon: <Award className="w-6 h-6" />, title: "Quality Excellence", desc: "Quality is the foundation of every product we deliver — market leadership through superior craftsmanship." },
              { icon: <ShieldCheck className="w-6 h-6" />, title: "Safety & Reliability", desc: "Every unit undergoes rigorous safety testing per IS 14665 and EN 81 standards." },
              { icon: <Sparkles className="w-6 h-6" />, title: "Innovative Thinking", desc: "A hand-picked team of engineers bringing modern IoT and web-app monitoring into every lift." },
            ].map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}>
                <div className="v2-forest inline-flex mb-6 border border-current p-3">{p.icon}</div>
                <h3 className="v2-serif text-2xl mb-3" style={{ color: "#1a1a1a" }}>{p.title}</h3>
                <p className="text-[#8a8a8a] leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ CRAFTSMANSHIP STORY ═══════════════════ */}
      <section className="v2-bg-cream py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1 }}
            src={MEDIA.productElevator}
            alt="Vila elevator interior craftsmanship"
            className="w-full aspect-[4/5] object-cover"
          />
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <div className="v2-eyebrow mb-6">Vila</div>
            <h2 className="v2-serif font-light leading-[1.05] text-4xl md:text-5xl mb-8" style={{ color: "#1a1a1a" }}>
              Elevating the Future<br />
              <span className="v2-italic-serif" style={{ color: "#0f5132" }}>of Vertical Transportation</span>
            </h2>
            <p className="text-[#4a4a4a] leading-relaxed mb-6 text-lg font-light">
              At Vila, we are redefining the standards of vertical transportation. Every elevator we produce blends cutting-edge engineering with the enduring values of South Indian craftsmanship. From our facility in Bommasandra, we design and deliver lifts that go beyond mere utility — enhancing spaces and enriching lifestyles.
            </p>
            <p className="text-[#4a4a4a] leading-relaxed mb-10 text-lg font-light">
              <em className="v2-italic-serif">Where craftsmanship meets innovation.</em> Every Vila elevator exemplifies meticulous engineering. Our dedicated in-house R&amp;D team ensures reliability, elegance, and advanced performance through rigorous testing.
            </p>
            <div className="v2-hairline mb-8" />
            <div className="grid grid-cols-3 gap-6 mb-10">
              <div>
                <div className="v2-serif font-normal text-3xl" style={{ color: "#0f5132" }}>15+</div>
                <div className="text-xs uppercase tracking-widest text-[#8a8a8a] mt-2">Years of Craft</div>
              </div>
              <div>
                <div className="v2-serif font-normal text-3xl" style={{ color: "#0f5132" }}>4</div>
                <div className="text-xs uppercase tracking-widest text-[#8a8a8a] mt-2">States Served</div>
              </div>
              <div>
                <div className="v2-serif font-normal text-3xl" style={{ color: "#0f5132" }}>100%</div>
                <div className="text-xs uppercase tracking-widest text-[#8a8a8a] mt-2">In-House</div>
              </div>
            </div>
            <Link to="/manufacturing" className="v2-btn-primary inline-block">Discover Manufacturing</Link>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ PRODUCT COLLECTION ═══════════════════ */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <div className="v2-eyebrow mb-6">Our Curated Range</div>
            <h2 className="v2-serif font-light text-4xl md:text-6xl leading-[1.05]" style={{ color: "#1a1a1a" }}>
              Engineered for <span className="v2-italic-serif" style={{ color: "#0f5132" }}>every scale.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PRODUCTS.map((p, i) => {
              const Icon = productIcons[p.icon] || Package;
              return (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.05 }}
                >
                  <Link to={`/products/${p.slug}`} className="block group">
                    <div className="relative aspect-[4/5] overflow-hidden mb-6">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                      <div className="absolute inset-0 bg-[#1a1a1a]/10 group-hover:bg-[#1a1a1a]/0 transition-colors duration-500" />
                    </div>
                    <div className="v2-eyebrow mb-3">{p.tag}</div>
                    <h3 className="v2-serif text-3xl mb-3 group-hover:text-[#0f5132] transition-colors" style={{ color: "#1a1a1a" }}>
                      {p.title}
                    </h3>
                    <p className="text-[#8a8a8a] leading-relaxed mb-4 font-light">{p.short.slice(0, 120)}…</p>
                    <div className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase" style={{ color: "#0f5132" }}>
                      Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ MANUFACTURING SHOWCASE ═══════════════════ */}
      <section className="v2-bg-cream py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
              <div className="v2-eyebrow mb-6">Our Manufacturing</div>
              <h2 className="v2-serif font-light text-4xl md:text-5xl leading-[1.05] mb-8" style={{ color: "#1a1a1a" }}>
                A rare distinction: <span className="v2-italic-serif" style={{ color: "#0f5132" }}>everything in-house.</span>
              </h2>
              <p className="text-[#4a4a4a] leading-relaxed mb-6 text-lg font-light">
                Most Indian elevator brands are assemblers. Vila is a manufacturer. Our Bommasandra facility houses a <strong>220-ton, 8+1 axis CNC press brake</strong>, <strong>fiber-laser sheet cutter</strong>, and <strong>robotic laser welding</strong> — allowing us to hold sub-millimetre tolerances end-to-end.
              </p>
              <div className="grid grid-cols-2 gap-4 my-10">
                <div className="border-l-2 v2-forest pl-4">
                  <div className="v2-serif text-3xl" style={{ color: "#0f5132" }}>±0.02mm</div>
                  <div className="text-xs uppercase tracking-widest text-[#8a8a8a] mt-1">Bending Accuracy</div>
                </div>
                <div className="border-l-2 v2-forest pl-4">
                  <div className="v2-serif text-3xl" style={{ color: "#0f5132" }}>220T</div>
                  <div className="text-xs uppercase tracking-widest text-[#8a8a8a] mt-1">Press-brake Force</div>
                </div>
              </div>
              <Link to="/manufacturing" className="v2-btn-primary inline-block">Tour The Facility</Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.1 }}>
              <div className="bg-white p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.2)]">
                <img src="/brand/cnc-press-brake.png" alt="220-ton CNC press brake at Vila facility" className="w-full aspect-[4/3] object-contain" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ SMART ELEVATOR ═══════════════════ */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
              <div className="v2-eyebrow mb-6">Smart Elevator · IoT-Enabled</div>
              <h2 className="v2-serif font-light text-4xl md:text-5xl leading-[1.05] mb-8" style={{ color: "#1a1a1a" }}>
                Monitor your elevator<br />
                <span className="v2-italic-serif" style={{ color: "#0f5132" }}>from anywhere.</span>
              </h2>
              <p className="text-[#4a4a4a] leading-relaxed mb-8 text-lg font-light">
                Every Vila elevator is powered by an industry-leading intelligent controller. Pair it with the Vila web app and you get real-time visibility into your elevator's health, ride analytics, and instant maintenance alerts — <em className="v2-italic-serif">right from anywhere, no download needed.</em>
              </p>
              <div className="grid grid-cols-2 gap-5 mb-10">
                {[
                  { icon: <Sparkles className="w-4 h-4" />, t: "Real-time Health" },
                  { icon: <Cpu className="w-4 h-4" />, t: "Instant Alerts" },
                  { icon: <Wrench className="w-4 h-4" />, t: "Remote Diagnostics" },
                  { icon: <Cog className="w-4 h-4" />, t: "Ride History" },
                ].map((f) => (
                  <div key={f.t} className="flex items-center gap-3">
                    <div className="v2-forest">{f.icon}</div>
                    <div className="text-sm text-[#4a4a4a] font-medium">{f.t}</div>
                  </div>
                ))}
              </div>
              <Link to="/products/passenger-elevators" className="v2-btn-primary inline-block">See Smart Elevators</Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.1 }} className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="w-72 bg-white border-2 border-[#e5ddd0] rounded-[42px] p-4 shadow-[0_30px_80px_-30px_rgba(15,81,50,0.4)]">
                  <div className="w-24 h-6 bg-slate-100 rounded-full mx-auto mb-4" />
                  <div className="bg-slate-50 rounded-[28px] p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Building A</div>
                        <div className="v2-serif font-medium text-[#1a1a1a] text-lg">Passenger Lift #1</div>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <div className="w-2 h-2 rounded-full bg-[#b08d57] animate-pulse" />
                        <span className="text-[#b08d57] font-semibold">Live</span>
                      </div>
                    </div>
                    <div className="bg-white border border-[#e5ddd0] p-4">
                      <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest mb-1">Current Floor</div>
                      <div className="v2-serif text-5xl" style={{ color: "#0f5132" }}>7</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white border border-[#e5ddd0] p-3">
                        <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Rides</div>
                        <div className="v2-serif text-xl text-[#1a1a1a]">142</div>
                      </div>
                      <div className="bg-white border border-[#e5ddd0] p-3">
                        <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Health</div>
                        <div className="v2-serif text-xl" style={{ color: "#0f5132" }}>98%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ GALLERY PREVIEW ═══════════════════ */}
      <section className="v2-bg-cream py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <div className="v2-eyebrow mb-4">Experience</div>
              <h2 className="v2-serif font-light text-4xl md:text-5xl leading-[1.05]" style={{ color: "#1a1a1a" }}>
                Our curated <span className="v2-italic-serif" style={{ color: "#0f5132" }}>installations.</span>
              </h2>
            </div>
            <Link to="/products" className="v2-btn-secondary inline-block">Explore Gallery</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[MEDIA.productElevator, MEDIA.hero, MEDIA.productEscalator, MEDIA.hero, MEDIA.productElevator, MEDIA.productEscalator].map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="overflow-hidden aspect-square group cursor-pointer"
              >
                <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ BLOG ═══════════════════ */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <div className="v2-eyebrow mb-4">Our Blog</div>
              <h2 className="v2-serif font-light text-4xl md:text-5xl leading-[1.05]" style={{ color: "#1a1a1a" }}>
                Vertical transportation, <span className="v2-italic-serif" style={{ color: "#0f5132" }}>decoded.</span>
              </h2>
            </div>
            <Link to="/blog" className="v2-btn-secondary inline-block">All Articles</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {POSTS.slice(0, 4).map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <Link to={`/blog/${p.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden mb-5">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                  </div>
                  <div className="v2-eyebrow mb-3">{p.category}</div>
                  <h3 className="v2-serif text-xl leading-tight group-hover:text-[#0f5132] transition-colors" style={{ color: "#1a1a1a" }}>{p.title}</h3>
                  <div className="text-sm mt-4 font-medium tracking-wider uppercase" style={{ color: "#0f5132" }}>Read Article →</div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ CONTACT ═══════════════════ */}
      <section className="v2-bg-cream py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <div className="v2-eyebrow mb-6">Let Us Connect</div>
            <h2 className="v2-serif font-light text-4xl md:text-6xl leading-[1.05] mb-8" style={{ color: "#1a1a1a" }}>
              Elevate <span className="v2-italic-serif" style={{ color: "#0f5132" }}>your project.</span>
            </h2>
            <p className="text-[#4a4a4a] leading-relaxed mb-8 text-lg font-light">
              Share your building specifications — we'll respond within one business day with a tailored proposal.
            </p>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <Phone className="w-4 h-4 v2-forest" />
                <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#1a1a1a] hover:text-[#0f5132] transition-colors">{BUSINESS.phone}</a>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-4 h-4 v2-forest mt-1" />
                <div className="text-[#4a4a4a] leading-relaxed">
                  {BUSINESS.address.line1}<br />
                  {BUSINESS.address.line2}, {BUSINESS.address.city} – {BUSINESS.address.postal}
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="bg-white p-8 lg:p-10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.15)]">
            <h3 className="v2-serif text-2xl mb-6 text-[#1a1a1a]">Send an enquiry</h3>
            <ContactForm sourcePage="home-v2" />
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="py-12 border-t border-[#e5ddd0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between flex-wrap gap-4 text-sm text-[#8a8a8a]">
          <div className="v2-serif" style={{ color: "#0f5132" }}>Vila Elevators &amp; Escalators</div>
          <div className="text-xs text-[#8a8a8a]">© {new Date().getFullYear()} · Preview V2 · <Link to="/" className="underline hover:text-[#0f5132]">Back to current site</Link></div>
        </div>
      </footer>
    </div>
  );
}
